"use client";
import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { AudioLines, FileText, Image as ImageIcon, Info, Plus } from "lucide-react";
import {
   Button,
   Dialog,
   DialogContent,
   DialogFooter,
   DialogHeader,
   DialogTitle,
   Popover,
   PopoverContent,
   PopoverTrigger
} from "@/app/components/ui";
import {
   attachmentFormatGroups,
   attachmentPickerAccept,
   type AttachmentFormatGroupId,
   type AttachmentPickerKind,
   type BrowserChatAttachmentPolicy
} from "@/app/lib/constants";
import { cn } from "@/app/lib/utils";

const formatDurationLimit = (seconds: number) =>
   `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const GROUP_ICONS: Record<AttachmentFormatGroupId, typeof ImageIcon> = {
   image: ImageIcon,
   document: FileText,
   audio: AudioLines
};

type ChatInputAttachButtonProps = {
   disabled: boolean;
   chatAttachmentPolicy: BrowserChatAttachmentPolicy;
   onFilesSelected: (files: FileList | null) => void;
};

const attachButtonClassName =
   "h-8 w-8 shrink-0 rounded-full border border-border text-gray-500 hover:border-primary/40 hover:bg-primary/10 hover:text-primary dark:text-gray-400";

const menuRowClassName =
   "group h-8 w-full items-center justify-start gap-2 rounded-lg px-2 text-left font-normal hover:border-primary/40 hover:bg-primary/10 hover:text-primary";

const MenuRowIcon = ({ icon: Icon }: { icon: typeof ImageIcon }) => (
   <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground group-hover:bg-primary/15 group-hover:text-primary">
      <Icon className="h-3.5 w-3.5" />
   </span>
);

const AttachmentFormatsDialog = ({
   open,
   onOpenChange,
   chatAttachmentPolicy
}: {
   open: boolean;
   onOpenChange: (open: boolean) => void;
   chatAttachmentPolicy: BrowserChatAttachmentPolicy;
}) => {
   const translation = useTranslations("Home");
   const groups = attachmentFormatGroups(chatAttachmentPolicy);
   const hasPdf = groups.some((group) => group.labels.includes("PDF"));
   const hasTextFiles = groups.some(
      (group) =>
         group.id === "document" &&
         group.labels.some((label) => ["TXT", "MD", "MARKDOWN", "CSV"].includes(label))
   );
   const documentLimits = [
      ...(hasPdf
         ? [
              translation("attachmentFormatsPdfLimit", {
                 maxSizeMB: chatAttachmentPolicy.maxSizeMB.pdf
              })
           ]
         : []),
      ...(hasTextFiles
         ? [
              translation("attachmentFormatsTextLimit", {
                 maxSizeMB: chatAttachmentPolicy.maxSizeMB.text
              })
           ]
         : [])
   ];

   const headingFor = (id: AttachmentFormatGroupId) => {
      if (id === "image") {
         return translation("attachmentFormatsImage", {
            maxSizeMB: chatAttachmentPolicy.maxSizeMB.image
         });
      }
      if (id === "audio") {
         return translation("attachmentFormatsAudio", {
            maxSizeMB: chatAttachmentPolicy.maxSizeMB.audio,
            maxDuration: formatDurationLimit(chatAttachmentPolicy.maxAudioDurationSeconds)
         });
      }
      return documentLimits.length
         ? translation("attachmentFormatsDocumentsWithLimits", {
              limits: documentLimits.join(" · ")
           })
         : translation("attachmentFormatsDocuments");
   };

   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent
            hideCloseButton
            className="max-w-[22rem] gap-5 p-5 sm:max-w-[22rem]"
         >
            <DialogHeader className="text-left">
               <DialogTitle className="text-lg font-semibold">
                  {translation("attachmentFormatsTitle")}
               </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
               {groups.map((group) => {
                  const Icon = GROUP_ICONS[group.id];
                  return (
                     <div key={group.id} className="flex gap-3">
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 space-y-1">
                           <p className="text-sm font-semibold text-foreground">
                              {headingFor(group.id)}
                           </p>
                           <p className="text-xs tracking-wide text-muted-foreground">
                              {group.labels.join(" · ")}
                           </p>
                        </div>
                     </div>
                  );
               })}
            </div>
            <DialogFooter className="sm:justify-stretch">
               <Button
                  type="button"
                  className="w-full"
                  onClick={() => onOpenChange(false)}
               >
                  {translation("attachmentFormatsGotIt")}
               </Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export const ChatInputAttachButton = ({
   disabled,
   chatAttachmentPolicy,
   onFilesSelected
}: ChatInputAttachButtonProps) => {
   const translation = useTranslations("Home");
   const fileInputRef = useRef<HTMLInputElement>(null);
   const [isMenuOpen, setIsMenuOpen] = useState(false);
   const [isFormatsOpen, setIsFormatsOpen] = useState(false);
   const groups = attachmentFormatGroups(chatAttachmentPolicy);
   const canUploadDocument = groups.some((group) => group.id !== "audio");
   const canUploadAudio = groups.some((group) => group.id === "audio");

   const openPicker = (kind: AttachmentPickerKind) => {
      if (disabled) return;
      const input = fileInputRef.current;
      if (!input) return;
      input.accept = attachmentPickerAccept(chatAttachmentPolicy, kind);
      input.click();
      setIsMenuOpen(false);
   };

   const openFormats = () => {
      setIsMenuOpen(false);
      setIsFormatsOpen(true);
   };

   return (
      <>
         <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            accept={[
               ...chatAttachmentPolicy.allowedMimeTypes,
               ...chatAttachmentPolicy.allowedExtensions
            ].join(",")}
            onChange={(event) => {
               onFilesSelected(event.target.files);
               event.target.value = "";
            }}
         />
         <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <PopoverTrigger asChild>
               <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={translation("attachFile")}
                  aria-disabled={disabled}
                  className={cn(attachButtonClassName, disabled && "opacity-50")}
               >
                  <Plus size={16} />
               </Button>
            </PopoverTrigger>
            <PopoverContent
               side="top"
               align="start"
               className="w-max max-w-[calc(100vw-2rem)] rounded-2xl border border-border p-1 shadow-lg"
            >
               <div className="flex flex-col gap-1">
                  {canUploadDocument && (
                     <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        className={menuRowClassName}
                        onClick={() => openPicker("document")}
                     >
                        <MenuRowIcon icon={ImageIcon} />
                        <span className="whitespace-nowrap text-sm font-medium text-foreground group-hover:text-primary">
                           {translation("attachmentUploadImageOrDocument")}
                        </span>
                     </Button>
                  )}
                  {canUploadAudio && (
                     <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        className={menuRowClassName}
                        onClick={() => openPicker("audio")}
                     >
                        <MenuRowIcon icon={AudioLines} />
                        <span className="whitespace-nowrap text-sm font-medium text-foreground group-hover:text-primary">
                           {translation("attachmentUploadAudio")}
                        </span>
                     </Button>
                  )}
                  {!canUploadDocument && !canUploadAudio && (
                     <p className="px-2 py-1.5 text-sm text-muted-foreground">
                        {translation("attachmentHintNoTypes")}
                     </p>
                  )}
                  {groups.length > 0 && (
                     <>
                        <div className="mx-2 h-px bg-border" />
                        <Button
                           type="button"
                           variant="ghost"
                           size="sm"
                           className={menuRowClassName}
                           onClick={openFormats}
                        >
                           <span className="flex w-6 shrink-0 items-center justify-center text-muted-foreground group-hover:text-primary">
                              <Info className="h-3.5 w-3.5" />
                           </span>
                           <span className="text-xs font-normal text-muted-foreground group-hover:text-primary">
                              {translation("attachmentViewSupportedFormats")}
                           </span>
                        </Button>
                     </>
                  )}
               </div>
            </PopoverContent>
         </Popover>
         <AttachmentFormatsDialog
            open={isFormatsOpen}
            onOpenChange={setIsFormatsOpen}
            chatAttachmentPolicy={chatAttachmentPolicy}
         />
      </>
   );
};
